import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { reaction } from 'mobx'
import PropTypes from 'prop-types'
import moment from 'moment'

import ProjectSearchResults from './ProjectSearchResults'
import { Autocomplete, Dropdown, TagsInput } from '@components/Autocomplete'
import Checkbox from '@components/Checkbox'
import { DatePickerRange } from '@components/DatePicker'
import { buildNestedOpts } from '@atheneum/api/dist/lib'
import Scroll from '@components/Scroll'
import { SearchPanelContainer } from '@components/Search'
import CollapsibleFilterGroup from '@components/CollapsibleFilterGroup'
import FilterGroup from '@components/FilterGroup'
import { StatusFilter } from '@components/StatusFilter'

const openIn = [
  {
    id: '',
    label: 'Details'
  },
  {
    id: '/experts-pipeline',
    label: 'Experts pipeline'
  },
  {
    id: '/client-view',
    label: 'Client view'
  },
  {
    id: '/outcome',
    label: 'Outcome'
  },
  {
    id: '/invoice',
    label: 'Invoice'
  },
  {
    id: '/logs',
    label: 'Logs'
  }
]

class ProjectSearchPanel extends Component {
  static propTypes = {
    projectSearchStore: PropTypes.object,
    modalStore: PropTypes.object,
    staticData: PropTypes.object
  }

  constructor (props) {
    super(props)
    this.registerReactions()
    this.search()
  }

  search = () => {
    return this.props.projectSearchStore.search()
  }

  searchOnEnter = e => {
    if (e.key === 'Enter') {
      return this.search()
    }
  }

  registerReactions () {
    this.reactions = []
    ;['country', 'status', 'industry'].forEach(key => {
      const query = this.props.projectSearchStore.query[key] || []
      this.reactions.push(
        reaction(
          () => query.map(i => i.value),
          () => this.search()
        )
      )
    })

    this.reactions.push(
      reaction(
        () =>
          [
            'startDate',
            'endDate',
            'account',
            'employee',
            'onlyMy',
            'bookmarked',
            'type',
            'hasSubmitted',
            'replyStatus',
            'eplStatus',
            'sortBy'
          ].map(i => this.props.projectSearchStore.query[i]),
        () => this.search()
      )
    )
  }

  onClearSearch = () => {
    this.props.projectSearchStore.clear()
  }

  renderCountryOrRegionItem = item => {
    if (item.type === 'countries') {
      return <span>{item.label}</span>
    }

    if (item.type === 'expertFocusRegions') {
      return <strong>{item.label}</strong>
    }

    return <span>{item.label}</span>
  }

  renderIndustryOrSubindustry = item => {
    if (item.type === 'subIndustries') {
      return <span>{item.label}</span>
    }

    if (item.type === 'industries') {
      return <strong>{item.label}</strong>
    }

    return <span>{item.label}</span>
  }

  render () {
    return (
      <div className="expert-search-panel">
        <div className="expert-search-panel-container">
          <SearchPanelContainer
            thin
            buttonProps={{
              dataCy: 'create-project-btn',
              buttonText: 'Create New Project',
              onClick: () =>
                this.props.modalStore.show({
                  type: 'PROJECT_FORM',
                  props: {
                    title: 'Set EPL status to Canceled',
                    cancelText: 'Cancel',
                    okText: 'Create'
                  }
                }),
              roles: [
                'FeeManagement',
                'Accounting',
                'AdvancedExpertManagement',
                'ProjectManagement',
                'AdvancedReporting',
                'Admin'
              ]
            }}
            searchTitle="Filter Projects"
            onClearSearch={this.onClearSearch}>
            <FilterGroup
              label="Project name, code, scope..."
              tooltipContent={
                <p>Use quotation marks to find exact matches only</p>
              }
              onSearch={() => this.search()}>
              <input
                onKeyUp={this.searchOnEnter}
                onChange={e =>
                  (this.props.projectSearchStore.query.keyword = e.target.value)
                }
                value={this.props.projectSearchStore.query.keyword}
              />
            </FilterGroup>
            <FilterGroup label="Client Account">
              <Autocomplete
                creatable
                itemFromCreatable={item => ({ label: item, value: item })}
                itemToString={item => item && item.label}
                placeholder="Select client account"
                loadOptions={inputValue =>
                  this.props.projectSearchStore
                    .autocomplete(inputValue, 'account')
                    .then(results => results.options)
                }
                onChange={val => {
                  this.props.projectSearchStore.query.account = val
                }}
                value={this.props.projectSearchStore.query.account}
              />
            </FilterGroup>
            <FilterGroup label="Atheneum Contact">
              <Autocomplete
                creatable
                itemFromCreatable={item => ({ label: item, value: item })}
                itemToString={item => item && item.label}
                placeholder="Select Atheneum contact"
                loadOptions={inputValue =>
                  this.props.projectSearchStore
                    .autocomplete(inputValue, 'employee')
                    .then(results => results.options)
                }
                onChange={val => {
                  this.props.projectSearchStore.query.employee = val
                }}
                value={this.props.projectSearchStore.query.employee}
              />
            </FilterGroup>
            <FilterGroup>
              <Checkbox
                label="Only my projects"
                value={this.props.projectSearchStore.query.onlyMy}
                onChange={val =>
                  (this.props.projectSearchStore.query.onlyMy = val)
                }
              />
              <Checkbox
                label="Only bookmarked projects"
                value={this.props.projectSearchStore.query.bookmarked}
                onChange={val =>
                  (this.props.projectSearchStore.query.bookmarked = val)
                }
              />
            </FilterGroup>
            <CollapsibleFilterGroup title="Geography" open>
              <div>
                <TagsInput
                  value={this.props.projectSearchStore.query.country}
                  placeholder="Select country or region"
                  options={buildNestedOpts(
                    'expertFocusRegions',
                    'countries',
                    'expertFocusRegionId'
                  )}
                  filterItemsOnly
                  itemToString={item => item && item.label}
                  renderItem={this.renderCountryOrRegionItem}
                  onChange={selectedCountries => {
                    this.props.projectSearchStore.query.country = selectedCountries
                    this.props.projectSearchStore.search()
                  }}
                />
              </div>
            </CollapsibleFilterGroup>
            <CollapsibleFilterGroup title="Industries" open>
              <div>
                <TagsInput
                  value={this.props.projectSearchStore.query.industry}
                  placeholder="Select industry"
                  options={buildNestedOpts(
                    'industries',
                    'subIndustries',
                    'industryId'
                  )}
                  filterItemsOnly
                  itemToString={item => item && item.label}
                  renderItem={this.renderIndustryOrSubindustry}
                  onChange={value => {
                    this.props.projectSearchStore.query.industry = value
                    this.props.projectSearchStore.search()
                  }}
                />
              </div>
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="More options" open>
              <FilterGroup label="Project Type">
                <Dropdown
                  selectedItem={this.props.projectSearchStore.query.type}
                  itemToString={item => item && item.name}
                  options={this.props.staticData.projectTypes}
                  onChange={option =>
                    (this.props.projectSearchStore.query.type = option)
                  }
                />
              </FilterGroup>
              <FilterGroup label="Project active between dates">
                <DatePickerRange
                  className="datepicker-range__search-panel"
                  fromDate={this.props.projectSearchStore.query.startDate}
                  toDate={this.props.projectSearchStore.query.endDate}
                  maxDate={moment().format('YYYY-MM-DD')}
                  onFromChange={val =>
                    (this.props.projectSearchStore.query.startDate = val)
                  }
                  onToChange={val =>
                    (this.props.projectSearchStore.query.endDate = val)
                  }
                />
              </FilterGroup>
              <FilterGroup label="Project status">
                <StatusFilter
                  type="project"
                  selectedItems={this.props.projectSearchStore.query.statusIds}
                  onChange={this.props.projectSearchStore.changeStatus}
                />
              </FilterGroup>

              <FilterGroup label="EPL status">
                <div>
                  <TagsInput
                    value={this.props.projectSearchStore.query.eplStatus}
                    placeholder="EPL reply status"
                    options={this.props.staticData.eplStatuses}
                    filterItemsOnly
                    itemToString={item => item && item.name}
                    renderItem={item => item.name}
                    onChange={value =>
                      (this.props.projectSearchStore.query.eplStatus = value)
                    }
                  />
                </div>
              </FilterGroup>
              <FilterGroup label="EPL reply status">
                <div>
                  <TagsInput
                    value={this.props.projectSearchStore.query.replyStatus}
                    placeholder="EPL reply status"
                    options={this.props.staticData.replyStatuses}
                    filterItemsOnly
                    itemToString={item => item && item.name}
                    renderItem={item => item.name}
                    onChange={value =>
                      (this.props.projectSearchStore.query.replyStatus = value)
                    }
                  />
                </div>
              </FilterGroup>
            </CollapsibleFilterGroup>
            <CollapsibleFilterGroup title="Settings">
              <FilterGroup label="Open projects in">
                <Dropdown
                  valueIsId
                  selectedItem={this.props.projectSearchStore.openIn}
                  itemToString={item => item && item.label}
                  options={openIn}
                  onChange={e => this.props.projectSearchStore.setOpenIn(e.id)}
                />
              </FilterGroup>
            </CollapsibleFilterGroup>
          </SearchPanelContainer>
          <div className="search-panel-results">
            <Scroll>
              <ProjectSearchResults />
            </Scroll>
          </div>
        </div>
      </div>
    )
  }
}

export default inject(
  'projectSearchStore',
  'staticData',
  'modalStore'
)(observer(ProjectSearchPanel))
